import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "@material-tailwind/react";
import { EyeIcon, FolderPlusIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import FormField from "../components/FormField";
import FormSelect from "../components/FormSelect";
import LoadingScreen from "../components/LoadingScreen";
import SectionCard from "../components/SectionCard";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import { deleteProject, saveProject, subscribeByOwner } from "../services/db";
import { formatMoneyWithUnit, toEnglishDigits } from "../utils/format";
import { calculateProjectProgress, calculateProjectsOverview } from "../utils/report";

const initialForm = {
  title: "",
  clientName: "",
  totalValue: "",
  receivedAmount: "",
  startDate: new Date().toISOString().slice(0, 10),
  dueDate: "",
  status: "active",
  description: ""
};

const STATUS_OPTIONS = [
  { value: "active", label: "نشط" },
  { value: "completed", label: "مكتمل" },
  { value: "on-hold", label: "معلق" }
];

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [alert, setAlert] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubscribers = [
      subscribeByOwner("projects", user.uid, setProjects),
      subscribeByOwner("projectTasks", user.uid, setTasks),
      subscribeByOwner("projectPayments", user.uid, setPayments)
    ];
    const timer = setTimeout(() => setReady(true), 250);
    return () => {
      unsubscribers.forEach((unsub) => unsub?.());
      clearTimeout(timer);
    };
  }, [user?.uid]);

  const overview = useMemo(() => calculateProjectsOverview(projects), [projects]);

  const projectCards = useMemo(() => [...projects].sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0)).map((project) => ({ ...project, progress: calculateProjectProgress(tasks.filter((task) => task.projectId === project.id)) })), [projects, tasks]);

  const resetForm = () => {
    setForm(initialForm);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    try {
      setBusy(true);
      await saveProject({ ...form, ownerUid: user.uid }, editId);
      setAlert({ color: "green", message: editId ? "تم تعديل المشروع." : "تمت إضافة المشروع." });
      resetForm();
    } catch (err) {
      setAlert({ color: "red", message: err?.message || "تعذر حفظ المشروع." });
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      title: item.title || "",
      clientName: item.clientName || "",
      totalValue: toEnglishDigits(String(item.totalValue || "")),
      receivedAmount: toEnglishDigits(String(item.receivedAmount || "")),
      startDate: item.startDate || new Date().toISOString().slice(0, 10),
      dueDate: item.dueDate || "",
      status: item.status || "active",
      description: item.description || ""
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("هل تريد حذف المشروع وكل مهامه ودفعاته؟")) return;
    try {
      await deleteProject(projectId, user.uid, tasks, payments);
      setAlert({ color: "green", message: "تم حذف المشروع." });
    } catch {
      setAlert({ color: "red", message: "تعذر حذف المشروع." });
    }
  };

  if (!ready) return <LoadingScreen text="جارٍ تحميل المشاريع..." />;

  return (
    <div className="page-shell space-y-4">
      <section className="mini-grid">
        <StatCard title="عدد المشاريع" value={toEnglishDigits(projects.length)} tone="brand" />
        <StatCard title="القيمة الكلية" value={formatMoneyWithUnit(overview.totalValue)} tone="accent" />
        <StatCard title="المستلم" value={formatMoneyWithUnit(overview.received)} tone="success" />
        <StatCard title="المتبقي" value={formatMoneyWithUnit(overview.remaining)} tone="danger" />
      </section>

      <SectionCard title={editId ? "تعديل مشروع" : "إضافة مشروع جديد"} subtitle="أدخل تفاصيل المشروع وقيمته والدفعة المستلمة حالياً">
        {alert ? <Alert color={alert.color} className="mb-4 rounded-2xl text-sm">{alert.message}</Alert> : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-grid">
            <FormField label="اسم المشروع" placeholder="مثال: تصميم متجر إلكتروني" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} required />
            <FormField label="اسم العميل" placeholder="اختياري" value={form.clientName} onChange={(e) => setForm((prev) => ({ ...prev, clientName: e.target.value }))} />
            <FormField type="number" inputMode="decimal" dir="ltr" label="قيمة المشروع" placeholder="0" value={form.totalValue} onChange={(e) => setForm((prev) => ({ ...prev, totalValue: e.target.value }))} required />
            <FormField type="number" inputMode="decimal" dir="ltr" label="المبلغ المستلم" placeholder="0" value={form.receivedAmount} onChange={(e) => setForm((prev) => ({ ...prev, receivedAmount: e.target.value }))} required />
            <FormField type="date" dir="ltr" label="تاريخ البداية" value={form.startDate} onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))} />
            <FormField type="date" dir="ltr" label="الموعد المتوقع" value={form.dueDate} onChange={(e) => setForm((prev) => ({ ...prev, dueDate: e.target.value }))} />
            <FormSelect label="الحالة" value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))} options={STATUS_OPTIONS} />
            <div className="sm:col-span-2 xl:col-span-3">
              <FormField as="textarea" label="تفاصيل المشروع" placeholder="ملخص العمل، الملاحظات، المطلوب من المشروع..." value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="submit" className="rounded-2xl bg-brand-700" disabled={busy}><span className="flex items-center gap-2"><FolderPlusIcon className="h-5 w-5" />{editId ? "حفظ التعديل" : "إضافة المشروع"}</span></Button>
            {editId ? <Button type="button" variant="outlined" className="rounded-2xl border-brand-200 text-brand-700" onClick={resetForm}>إلغاء</Button> : null}
          </div>
        </form>
      </SectionCard>

      <SectionCard title="قائمة المشاريع" subtitle="افتح كل مشروع لمتابعة الدفعات والمهام والإنجاز">
        {!projectCards.length ? (
          <EmptyState title="لا توجد مشاريع" description="أضف مشروعك الأول ليظهر هنا." />
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {projectCards.map((project) => (
              <div key={project.id} className="mobile-card">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-extrabold text-brand-800 sm:text-lg">{project.title}</h3>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${project.status === "completed" ? "bg-emerald-50 text-income" : project.status === "on-hold" ? "bg-amber-50 text-amber-700" : "bg-brand-50 text-brand-700"}`}>
                        {project.status === "completed" ? "مكتمل" : project.status === "on-hold" ? "معلق" : "نشط"}
                      </span>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600 sm:text-sm">
                      <p>العميل: {project.clientName || "—"}</p>
                      <p>البداية: {project.startDate || "—"}</p>
                      <p>الموعد: {project.dueDate || "—"}</p>
                      <p>الإنجاز: {toEnglishDigits(project.progress)}%</p>
                    </div>

                    {project.description ? <p className="mt-3 text-sm leading-7 text-slate-500">{project.description}</p> : null}

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="rounded-2xl bg-slate-50 p-3"><div className="text-[11px] text-slate-500">القيمة الكلية</div><div className="mt-1 font-extrabold">{formatMoneyWithUnit(project.totalValue)}</div></div>
                      <div className="rounded-2xl bg-emerald-50 p-3"><div className="text-[11px] text-slate-500">المستلم</div><div className="mt-1 font-extrabold text-income">{formatMoneyWithUnit(project.receivedAmount)}</div></div>
                      <div className="rounded-2xl bg-amber-50 p-3"><div className="text-[11px] text-slate-500">المتبقي</div><div className="mt-1 font-extrabold text-amber-800">{formatMoneyWithUnit(project.remainingAmount)}</div></div>
                    </div>

                    <div className="mt-4">
                      <div className="mb-2 flex items-center justify-between text-[11px] font-extrabold text-slate-500"><span>نسبة إنجاز المهام</span><span>{toEnglishDigits(project.progress)}%</span></div>
                      <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-gradient-to-r from-brand-700 to-accent-500" style={{ width: `${project.progress}%` }} /></div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 self-start sm:flex-col">
                    <Link to={`/projects/${project.id}`}><Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-brand-200 text-brand-700"><EyeIcon className="h-4 w-4" />فتح</Button></Link>
                    <Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-brand-200 text-brand-700" onClick={() => handleEdit(project)}><PencilSquareIcon className="h-4 w-4" />تعديل</Button>
                    <Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-red-200 text-red-600" onClick={() => handleDelete(project.id)}><TrashIcon className="h-4 w-4" />حذف</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
